import backgroundImage from "../assets/background_test.jpg";
import backgroundImageBlue from "../assets/background_test_blue.jpg";

export interface ColorTheme {
  name: string;
  background_1: string;
  background_2: string;
  background_3: string;
  main_background: string;
  background_image?: string; // Optional background image URL
  background_image_style?: {
    repeat?: string;
    attachment?: string;
    size?: string;
    position?: string;
  };
  background_selected: string;
  background_unselected: string;
  text_1: string;
  text_2: string;
  text_3: string;
  text_selected: string;
  text_unselected: string;
  header_text: string;
  detail_text: string;
  button_text: string;
  button_color: string;
  field_name_color: string;
  error_text_color: string;
  header_border_color: string;
  header_border_mobile_color: string;
}

export const themes: Record<string, ColorTheme> = {
  dark: {
    name: "Dark",
    background_1: "#333333",
    background_2: "#2e2e2e",
    background_3: "#000000",
    main_background: "#000000",
    background_image: backgroundImage,
    background_image_style: {
      repeat: "no-repeat",
      attachment: "fixed",
      size: "cover",
      position: "center",
    },
    background_selected: "#00bf62",
    background_unselected: "#000000",
    text_1: "#000000",
    text_2: "#000000",
    text_3: "#00bf62",
    text_selected: "#000000",
    text_unselected: "#00bf62",
    header_text: "#00bf62",
    detail_text: "#ffffff",
    button_text: "#000000",
    button_color: "#007bff",
    field_name_color: "#ffffff",
    error_text_color: "#ff0000",
    header_border_color: "#00bf62",
    header_border_mobile_color: "#00bf62",
  },
  light: {
    name: "Light",
    background_1: "#e0e0e0",
    background_2: "#f5f5f5",
    background_3: "#ffffff",
    main_background: "#ffffff",
    background_image: backgroundImage,
    background_image_style: {
      repeat: "no-repeat",
      attachment: "fixed",
      size: "cover",
      position: "center",
    },
    background_selected: "#007bff",
    background_unselected: "#e0e0e0",
    text_1: "#000000",
    text_2: "#333333",
    text_3: "#007bff",
    text_selected: "#ffffff",
    text_unselected: "#007bff",
    header_text: "#007bff",
    detail_text: "#333333",
    button_text: "#ffffff",
    button_color: "#007bff",
    field_name_color: "#333333",
    error_text_color: "#ff0000",
    header_border_color: "#007bff",
    header_border_mobile_color: "#007bff",
  },
  blue: {
    //#0070eb
    name: "Blue",
    background_1: "#0070eb",
    background_2: "#1a365d",
    background_3: "#0d1f3c",
    main_background: "#0d1f3c",
    background_image: backgroundImageBlue,
    background_image_style: {
      repeat: "no-repeat",
      attachment: "fixed",
      size: "cover",
      position: "center",
    },
    background_selected: "#63b3ed",
    background_unselected: "#1a365d",
    text_1: "#ffffff",
    text_2: "#e0e0e0",
    text_3: "#63b3ed",
    text_selected: "#000000",
    text_unselected: "#63b3ed",
    header_text: "#63b3ed",
    detail_text: "#ffffff",
    button_text: "#000000",
    button_color: "#63b3ed",
    field_name_color: "#ffffff",
    error_text_color: "#fc8181",
    header_border_color: "#63b3ed",
    header_border_mobile_color: "#63b3ed",
  },
  purple: {
    name: "Purple",
    background_1: "#6b46c1",
    background_2: "#553c9a",
    background_3: "#2d1b69",
    main_background: "#2d1b69",
    background_image: backgroundImage,
    background_image_style: {
      repeat: "no-repeat",
      attachment: "fixed",
      size: "cover",
      position: "center",
    },
    background_selected: "#b794f4",
    background_unselected: "#553c9a",
    text_1: "#ffffff",
    text_2: "#e0e0e0",
    text_3: "#b794f4",
    text_selected: "#000000",
    text_unselected: "#b794f4",
    header_text: "#b794f4",
    detail_text: "#ffffff",
    button_text: "#000000",
    button_color: "#b794f4",
    field_name_color: "#ffffff",
    error_text_color: "#fc8181",
    header_border_color: "#b794f4",
    header_border_mobile_color: "#b794f4",
  },
};

export const applyTheme = (themeKey: string): void => {
  const theme = themes[themeKey];
  if (!theme) return;

  const root = document.documentElement;

  // Update CSS custom properties
  root.style.setProperty("--background-1", theme.background_1);
  root.style.setProperty("--background-2", theme.background_2);
  root.style.setProperty("--background-3", theme.background_3);

  // Update the main background color variable used by body
  root.style.setProperty("--main-background", theme.main_background);

  // Apply background image to body if specified
  // We'll use a pseudo-element approach for transparency
  const body = document.body;

  // Remove any existing background image styling
  body.style.backgroundImage = "none";

  // Create or update the background image style element
  let bgImageStyle = document.getElementById("theme-background-image");
  if (!bgImageStyle) {
    bgImageStyle = document.createElement("style");
    bgImageStyle.id = "theme-background-image";
    document.head.appendChild(bgImageStyle);
  }

  if (theme.background_image) {
    bgImageStyle.textContent = `
      body::before {
        content: "";
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url(${theme.background_image});
        background-repeat: ${
          theme.background_image_style?.repeat || "no-repeat"
        };
        background-attachment: ${
          theme.background_image_style?.attachment || "fixed"
        };
        background-size: ${theme.background_image_style?.size || "cover"};
        background-position: ${
          theme.background_image_style?.position || "center"
        };
        opacity: 0.5;
        z-index: -1;
        pointer-events: none;
        filter: brightness(0.5);
      }
    `;
  } else {
    bgImageStyle.textContent = "";
  }

  // Update the CSS classes
  const style = document.createElement("style");
  style.id = "theme-override";

  // Remove any existing theme override
  const existingStyle = document.getElementById("theme-override");
  if (existingStyle) {
    existingStyle.remove();
  }

  style.textContent = `
    .background_1 {
      background-color: ${theme.background_1} !important;
    }
    .background_2 {
      background-color: ${theme.background_2} !important;
    }
    .background_3 {
      background-color: ${theme.background_3} !important;
    }
    .background_selected {
      background-color: ${theme.background_selected} !important;
    }
    .background_unselected {
      background-color: ${theme.background_unselected} !important;
    }
    .top-nav {
      // background-color: ${theme.background_1} !important;
    }
    .text_1 {
      color: ${theme.text_1} !important;
    }
    .text_2 {
      color: ${theme.text_2} !important;
    }
    .text_3 {
      color: ${theme.text_3} !important;
    }
    .text_selected {
      color: ${theme.text_selected} !important;
    }
    .text_unselected {
      color: ${theme.text_unselected} !important;
    }
    .header_text {
      color: ${theme.header_text} !important;
    }
    .detail_text {
      color: ${theme.detail_text} !important;
    }
    .button_text {
      color: ${theme.button_text} !important;
    }
    .button_color {
      background-color: ${theme.button_color} !important;
    }
    .field_name_color {
      color: ${theme.field_name_color} !important;
    }
    .error_text_color {
      color: ${theme.error_text_color} !important;
    }
    .header_border_color {
      border-bottom-color: ${theme.header_border_color} !important;
    }
    .header_border_mobile_color {
      border-bottom-color: ${theme.header_border_mobile_color} !important;
    }
  `;

  document.head.appendChild(style);

  // Save theme preference to localStorage
  localStorage.setItem("colorTheme", themeKey);
};

export const loadSavedTheme = (): void => {
  const savedTheme = localStorage.getItem("colorTheme");
  if (savedTheme && themes[savedTheme]) {
    applyTheme(savedTheme);
  } else {
    // Default to dark theme
    applyTheme("dark");
  }
};

export const getCurrentTheme = (): string => {
  return localStorage.getItem("colorTheme") || "dark";
};
